// Newsletter image + text block.
//
// Schema:
//   newsletterImageText  content: "newsletterText+"
//   attrs: id, layout (imageLeft|imageRight), imageWidth, src, alt
//
// Image is stored as block attrs (not as a child node). This makes the text
// slot a normal block-flow container: pressing Enter inside it produces a
// new paragraph that stacks vertically beneath the previous one, instead of
// becoming a sibling grid item. The NodeView renders the <img> itself from
// the attrs; PM only manages the text children inside contentDOM.
//
// renderHTML is valid (`['div', attrs, 0]`) — the content hole is the sole
// child of its parent array. The <img> appears in editor DOM via NodeView but
// is intentionally omitted from getHTML(); the server-side EmailRenderer
// emits the <img> for the email output.

import { Node, mergeAttributes } from "@tiptap/core"
import { NodeSelection } from "@tiptap/pm/state"

import { idAttributeSpec } from "./attributes"
import { buildSizeGroup, buildResizeHandle } from "./image"

const VALID_LAYOUTS = ["imageLeft", "imageRight"]
const LEGACY_WIDTHS = { narrow: 30, medium: 45, wide: 60 }
const IT_WIDTH_MIN = 15
const IT_WIDTH_MAX = 80

function normalizeItWidth(raw, fallback = 45) {
  if (raw === null || raw === undefined || raw === "") return fallback
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return clampIt(Math.round(raw))
  }
  const str = String(raw)
  if (LEGACY_WIDTHS[str] != null) return LEGACY_WIDTHS[str]
  const n = parseInt(str, 10)
  return Number.isFinite(n) ? clampIt(n) : fallback
}

function clampIt(v) {
  return Math.max(IT_WIDTH_MIN, Math.min(IT_WIDTH_MAX, v))
}

export const NewsletterImageText = Node.create({
  name: "newsletterImageText",
  group: "newsletterBlock",
  content: "newsletterText+",
  draggable: true,
  selectable: true,
  isolating: true,

  addOptions() {
    return { HTMLAttributes: {} }
  },

  addAttributes() {
    return {
      id: idAttributeSpec,
      layout: {
        default: "imageLeft",
        parseHTML: (el) => {
          const value = el.getAttribute("data-layout")
          return VALID_LAYOUTS.includes(value) ? value : "imageLeft"
        },
        renderHTML: (attrs) => ({ "data-layout": attrs.layout }),
      },
      imageWidth: {
        default: 45,
        parseHTML: (el) => normalizeItWidth(el.getAttribute("data-image-width")),
        renderHTML: (attrs) => ({ "data-image-width": String(normalizeItWidth(attrs.imageWidth)) }),
      },
      src: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-src") || "",
        renderHTML: (attrs) => (attrs.src ? { "data-src": attrs.src } : {}),
      },
      alt: {
        default: "",
        parseHTML: (el) => el.getAttribute("data-alt") || "",
        renderHTML: (attrs) => (attrs.alt ? { "data-alt": attrs.alt } : {}),
      },
    }
  },

  parseHTML() {
    return [{ tag: "div[data-newsletter-image-text]" }]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "div",
      mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
        "data-newsletter-image-text": "",
      }),
      0,
    ]
  },

  addNodeView() {
    return ({ node, editor, getPos }) => buildImageTextView(node, editor, getPos)
  },

  addCommands() {
    return {
      insertNewsletterImageText:
        ({ src = "", alt = "", layout = "imageLeft", imageWidth = "medium" } = {}) =>
        ({ state, dispatch, tr, view }) => {
          const blockType = state.schema.nodes.newsletterImageText
          const paragraphType = state.schema.nodes.newsletterParagraph
          if (!blockType || !paragraphType) return false

          const paragraph = paragraphType.createAndFill()
          if (!paragraph) return false

          const node = blockType.createAndFill({ src, alt, layout, imageWidth }, paragraph)
          if (!node) return false

          const $from = state.selection.$from
          const insertPos = $from.depth === 0 ? $from.pos : $from.after(1)

          if (!dispatch) return true

          tr.insert(insertPos, node)
          tr.setSelection(NodeSelection.create(tr.doc, insertPos))
          dispatch(tr.scrollIntoView())
          view?.focus()
          return true
        },

      setImageTextLayout:
        (pos, layout) =>
        ({ state, dispatch, tr }) => {
          if (!VALID_LAYOUTS.includes(layout)) return false
          const node = state.doc.nodeAt(pos)
          if (!node || node.type.name !== "newsletterImageText") return false
          if (!dispatch) return true
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, layout })
          dispatch(tr)
          return true
        },

      setImageTextWidth:
        (pos, imageWidth) =>
        ({ state, dispatch, tr }) => {
          const next = normalizeItWidth(imageWidth)
          const node = state.doc.nodeAt(pos)
          if (!node || node.type.name !== "newsletterImageText") return false
          if (!dispatch) return true
          tr.setNodeMarkup(pos, undefined, { ...node.attrs, imageWidth: next })
          dispatch(tr)
          return true
        },
    }
  },
})

function buildImageTextView(initialNode, editor, getPos) {
  const dom = document.createElement("div")
  dom.setAttribute("data-newsletter-image-text", "")
  dom.className = "nl-image-text"

  const img = document.createElement("img")
  img.className = "nl-image-text__image"
  img.contentEditable = "false"
  img.draggable = false

  const text = document.createElement("div")
  text.className = "nl-image-text__text"

  const layoutToggle = document.createElement("button")
  layoutToggle.type = "button"
  layoutToggle.className = "nl-image-text__layout-toggle"
  layoutToggle.textContent = "Swap layout"
  layoutToggle.contentEditable = "false"
  layoutToggle.addEventListener("mousedown", (event) => event.preventDefault())
  layoutToggle.addEventListener("click", (event) => {
    event.preventDefault()
    const pos = typeof getPos === "function" ? getPos() : null
    if (pos === null || pos === undefined) return
    const node = editor.state.doc.nodeAt(pos)
    if (!node) return
    const next = node.attrs.layout === "imageLeft" ? "imageRight" : "imageLeft"
    editor.chain().focus().setImageTextLayout(pos, next).run()
  })

  const sizeGroup = buildSizeGroup(
    [
      { value: 30, label: "S", title: "Narrow image" },
      { value: 45, label: "M", title: "Medium image" },
      { value: 60, label: "L", title: "Wide image" },
    ],
    (imageWidth) => {
      const pos = typeof getPos === "function" ? getPos() : null
      if (pos === null || pos === undefined) return
      editor.chain().focus().setImageTextWidth(pos, imageWidth).run()
    }
  )
  sizeGroup.classList.add("nl-image-text__size-group")

  const resizeHandle = buildResizeHandle({
    direction: "right",
    onResize: ({ delta, startWidth }) => {
      const pos = typeof getPos === "function" ? getPos() : null
      if (pos === null || pos === undefined) return
      const node = editor.state.doc.nodeAt(pos)
      const layout = node?.attrs.layout || "imageLeft"
      const container = dom.clientWidth || 600
      const startPx = (startWidth / 100) * container
      // imageRight layout: visual right edge moves toward text, so dragging
      // right should shrink the image. Flip delta accordingly.
      const signed = layout === "imageRight" ? -delta : delta
      const newPx = startPx + signed
      const newPercent = Math.round((newPx / container) * 100)
      editor.chain().focus().setImageTextWidth(pos, newPercent).run()
    },
    getStartWidth: () => normalizeItWidth(editor.state.doc.nodeAt(getPos())?.attrs.imageWidth),
  })
  resizeHandle.classList.add("nl-image-text__resize-handle")

  dom.append(img, text, layoutToggle, sizeGroup, resizeHandle)

  const refresh = (node) => {
    const layout = node.attrs.layout || "imageLeft"
    const imageWidth = normalizeItWidth(node.attrs.imageWidth)
    dom.dataset.layout = layout
    dom.dataset.imageWidth = String(imageWidth)
    dom.style.setProperty("--nl-image-text-w", `${imageWidth}%`)
    if (node.attrs.src) {
      img.src = node.attrs.src
      img.alt = node.attrs.alt || ""
      img.dataset.empty = "false"
    } else {
      img.removeAttribute("src")
      img.alt = ""
      img.dataset.empty = "true"
    }
    sizeGroup.querySelectorAll("[data-size]").forEach((btn) => {
      btn.dataset.active = Number(btn.dataset.size) === imageWidth ? "true" : "false"
    })
  }

  refresh(initialNode)

  return {
    dom,
    contentDOM: text,
    update(node) {
      if (node.type.name !== initialNode.type.name) return false
      refresh(node)
      return true
    },
    selectNode() {
      dom.classList.add("nl-image-text--selected")
    },
    deselectNode() {
      dom.classList.remove("nl-image-text--selected")
    },
    ignoreMutation(mutation) {
      return !text.contains(mutation.target)
    },
  }
}
